import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { useFormik } from 'formik';

import { Modal, ModalBodyFooter, Button, ModalLoading, ErrorMessage } from '../../custom-essentials';
import { SelectSingle, SelectMulti, FormikControl, Check, DatePicker } from '../../form';
import { renderChanges } from '../stamps/helpers';
import { TooltipWrapper } from '../../display';
import {
    validationSchema, renderRecommendedStamps, getSelectedInstructors,
    statusOptions, getInitSelectedStatus, getCenterOptions,
    getInitSelectedCenter, renderSubmitting, renderRelevantNotes,
    renderPastFlags,
    renderAnswerKeyButton
} from './helpers';
import { tagOptions as flagTags } from '../flags/helpers';
import { formatDateFull, formatTime, formatDateApi, checkLessonDatesValid } from '../../functions';
import { SVGIcon } from '../../custom-essentials';

function AppointmentLearningPlanEditor(props) {
    const {
        formik,
        lessons,
        updateLearningPlanItems,
        updateLearningPlan,
        refreshLearningPlans,
        setOriginalLPItemMap,
    } = props;

    const saveTimers = useRef({});
    const [savingById, setSavingById] = useState({});
    const [selectedLessonToAdd, setSelectedLessonToAdd] = useState({ value: -1, label: 'Select lesson...' });
    const [isAdding, setIsAdding] = useState(false);

    const selectedLP = formik.values.selectedLearningPlan?.obj || {};
    const selectedLPId = parseInt(formik.values.selectedLearningPlan?.value || -1);
    const lpItems = Array.isArray(selectedLP.items) ? selectedLP.items : [];

    const today = formatDateApi(new Date()); // YYYY-MM-DD

    const buildLearningPlanParams = useCallback(() => {
        if (!selectedLPId || selectedLPId === -1) return null;
        // Backend expects these fields on PUT /mp/learning-plans
        return {
            id: selectedLPId,
            assessmentId: ([null, undefined, '', 'null'].includes(selectedLP.assessment_id))
                ? -1
                : parseInt(selectedLP.assessment_id),
            name: selectedLP.name || '',
            student: selectedLP.student,
            notes: selectedLP.notes || '',
            isReview: parseInt(selectedLP.is_review || 0) === 1 ? 1 : 0,
            sortOrder: parseInt(selectedLP.sort_order || 0),
            active: parseInt(selectedLP.active || 0) === 1 ? 1 : 0,
        };
    }, [selectedLP, selectedLPId]);

    const buildLearningPlanItemsParams = useCallback((items) => {
        const sorted = [...(items || [])].sort((a, b) => parseInt(a.sort_order) - parseInt(b.sort_order));
        return sorted.map((row, idx) => ({
            lessonId: parseInt(row.lesson_id),
            dateAssigned: row.dateAssigned ? row.dateAssigned : null,
            dateCompleted: row.dateCompleted ? row.dateCompleted : null,
            sortOrder: idx + 1,
        }));
    }, []);

    const updateItemValue = useCallback((lpiId, field, nextValue) => {
        const newValues = { ...formik.values };
        const items = newValues.selectedLearningPlan?.obj?.items || [];
        const idx = items.findIndex(i => parseInt(i.id) === parseInt(lpiId));
        if (idx < 0) return;
        const v = nextValue || '';

        // Keep dates logically consistent:
        // - Completed cannot be before Assigned
        // - If the user moves Assigned later than Completed, move Completed along with it (instead of blocking edits)
        // - If the user sets Completed earlier than Assigned, move Assigned back to match
        if (field === 'dateAssigned') {
            items[idx].dateAssigned = v;
            const existingCompleted = items[idx].dateCompleted || '';
            if (v && existingCompleted && v > existingCompleted) {
                items[idx].dateCompleted = v;
            }
        }
        if (field === 'dateCompleted') {
            items[idx].dateCompleted = v;
            const existingAssigned = items[idx].dateAssigned || '';
            if (v && existingAssigned && v < existingAssigned) {
                items[idx].dateAssigned = v;
            }
        }
        newValues.selectedLearningPlan.obj.items = items;
        formik.setValues(newValues);

        if (saveTimers.current[lpiId]) clearTimeout(saveTimers.current[lpiId]);
        saveTimers.current[lpiId] = setTimeout(async () => {
            const row = items[idx];
            if (!row) return;

            const assigned = row.dateAssigned ? row.dateAssigned : null;
            const completed = row.dateCompleted ? row.dateCompleted : null;
            if (!checkLessonDatesValid(assigned, completed)) {
                formik.setStatus(`Invalid lesson dates for: ${row.name}. Date completed cannot be before date assigned.`);
                return;
            }

            setSavingById(prev => ({ ...prev, [lpiId]: true }));
            const resp = await updateLearningPlanItems({
                learningPlanItems: [{
                    id: parseInt(row.id),
                    lessonId: row.lesson_id,
                    dateAssigned: assigned,
                    dateCompleted: completed,
                    sortOrder: parseInt(row.sort_order),
                }]
            });

            const ok = resp && (resp.status === 200 || resp.status === 201);
            if (!ok) {
                console.error('[Appointment Details] Failed to save learning plan item:', resp);
                formik.setStatus('Failed to save learning plan item changes. Please try again.');
            } else if (typeof setOriginalLPItemMap === 'function') {
                setOriginalLPItemMap(prev => ({
                    ...prev,
                    [parseInt(row.id)]: { ...(prev[parseInt(row.id)] || {}), dateAssigned: row.dateAssigned, dateCompleted: row.dateCompleted }
                }));
            }

            setSavingById(prev => {
                const next = { ...prev };
                delete next[lpiId];
                return next;
            });
        }, 600);
    }, [formik, updateLearningPlanItems, setOriginalLPItemMap]);

    const handleAddLesson = useCallback(async () => {
        const lesson = selectedLessonToAdd;
        if (!selectedLPId || selectedLPId === -1) return;
        if (!lesson || lesson.value === -1) return;
        if (!updateLearningPlan) return;

        setIsAdding(true);
        const learningPlanParams = buildLearningPlanParams();
        if (!learningPlanParams) {
            setIsAdding(false);
            formik.setStatus('No learning plan is selected. Please select a plan and try again.');
            return;
        }

        // Backend contract: PUT /mp/learning-plans replaces ALL items, so submit full list (existing + new).
        const maxSort = lpItems.length ? Math.max(...lpItems.map(i => parseInt(i.sort_order))) : 0;
        const nextItems = [...lpItems, {
            id: `new-${lesson.value}-${Date.now()}`,
            lesson_id: parseInt(lesson.value),
            dateAssigned: '',
            dateCompleted: '',
            sort_order: maxSort + 1,
        }];
        const learningPlanItemsParams = buildLearningPlanItemsParams(nextItems);

        const resp = await updateLearningPlan({
            learningPlan: learningPlanParams,
            learningPlanItems: learningPlanItemsParams,
        });
        const ok = resp && (resp.status === 200 || resp.status === 201);
        if (!ok) {
            console.error('[Appointment Details] Failed to add lesson to plan:', resp);
            formik.setStatus('Failed to add lesson to learning plan. Please try again.');
        } else if (typeof refreshLearningPlans === 'function') {
            await refreshLearningPlans(selectedLPId);
            setSelectedLessonToAdd({ value: -1, label: 'Select lesson...' });
        }
        setIsAdding(false);
    }, [selectedLessonToAdd, selectedLPId, lpItems, updateLearningPlan, refreshLearningPlans, formik, buildLearningPlanParams, buildLearningPlanItemsParams]);

    const handleRemoveLesson = useCallback(async (lpiId) => {
        if (!selectedLPId || selectedLPId === -1) return;
        if (!updateLearningPlan) return;

        const learningPlanParams = buildLearningPlanParams();
        if (!learningPlanParams) {
            formik.setStatus('No learning plan is selected. Please select a plan and try again.');
            return;
        }

        const nextItems = lpItems.filter(i => parseInt(i.id) !== parseInt(lpiId));
        const learningPlanItemsParams = buildLearningPlanItemsParams(nextItems);

        const resp = await updateLearningPlan({
            learningPlan: learningPlanParams,
            learningPlanItems: learningPlanItemsParams,
        });
        const ok = resp && (resp.status === 200 || resp.status === 201);
        if (!ok) {
            console.error('[Appointment Details] Failed to remove lesson from plan:', resp);
            formik.setStatus('Failed to remove lesson from learning plan. Please try again.');
        } else if (typeof refreshLearningPlans === 'function') {
            await refreshLearningPlans(selectedLPId);
        }
    }, [selectedLPId, updateLearningPlan, refreshLearningPlans, formik, lpItems, buildLearningPlanParams, buildLearningPlanItemsParams]);

    const lessonOptions = useMemo(() => {
        const currentLessonIds = new Set(lpItems.map(i => parseInt(i.lesson_id)));
        const raw = Array.isArray(lessons) ? lessons : [];
        const opts = raw
            .filter(l => !currentLessonIds.has(parseInt(l.id)))
            .map(l => ({ value: parseInt(l.id), label: `${l.lesson_id}: ${l.name}` }));
        return [{ value: -1, label: 'Select lesson...' }, ...opts];
    }, [lessons, lpItems]);

    return (
        <div>
            <div className="flex flex-row items-end gap-x-2 mb-3">
                <div className="w-96">
                    <SelectSingle
                        id="details-add-lesson"
                        name="selectedLessonToAdd"
                        value={selectedLessonToAdd}
                        onChange={(e) => setSelectedLessonToAdd(e.target.value)}
                        options={lessonOptions}
                    />
                </div>
                <Button
                    color="lte-mpLBlue"
                    disabled={isAdding || !selectedLessonToAdd || selectedLessonToAdd.value === -1}
                    onClick={handleAddLesson}
                >
                    {isAdding ? 'Adding...' : 'Add Lesson'}
                </Button>
            </div>

            {!lpItems?.length ? (
                <div className="text-mpOrange">No active items</div>
            ) : (
                <div className="max-h-[40rem] overflow-y-auto">
                    {lpItems
                        .filter(lpi => !(formik.values.hideCompleted && lpi.dateCompleted))
                        .map((lpi) => {
                        const assigned = lpi.dateAssigned || '';
                        const completed = lpi.dateCompleted || '';
                        const shouldWarn = !checkLessonDatesValid(assigned || null, completed || null);

                        return (
                            <div key={`details-learning-plan-item-${lpi.id}`} className="mb-4 pb-4 border-b">
                                <div className="flex flex-row justify-between items-center">
                                    <h5 className="pr-4">{lpi.name}</h5>
                                    <div className="flex flex-row gap-x-2 items-center">
                                        <div>
                                            {renderAnswerKeyButton(lpi)}
                                        </div>
                                        <TooltipWrapper tooltipText="Remove this lesson from the learning plan">
                                            <Button
                                                color="lte-mpLRed"
                                                variant="icon"
                                                onClick={() => handleRemoveLesson(lpi.id)}
                                            >
                                                <SVGIcon fileName="trash" />
                                            </Button>
                                        </TooltipWrapper>
                                    </div>
                                </div>

                                <div className="flex flex-row gap-x-4 mt-2">
                                    <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                        <h6>Date Assigned</h6>
                                        <div className="flex flex-row gap-x-2 items-center">
                                            <DatePicker
                                                id={`details-lpi-${lpi.id}-assigned`}
                                                name="dateAssigned"
                                                value={assigned}
                                                onChange={(e) => updateItemValue(lpi.id, 'dateAssigned', e.target.value)}
                                            />
                                            <Button
                                                color="lte-mpLBlue"
                                                onClick={() => updateItemValue(lpi.id, 'dateAssigned', today)}
                                            >
                                                Today
                                            </Button>
                                            <Button
                                                color="lte-mpLRed"
                                                onClick={() => updateItemValue(lpi.id, 'dateAssigned', '')}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                        <h6>Date Completed</h6>
                                        <div className="flex flex-row gap-x-2 items-center">
                                            <DatePicker
                                                id={`details-lpi-${lpi.id}-completed`}
                                                name="dateCompleted"
                                                value={completed}
                                                onChange={(e) => updateItemValue(lpi.id, 'dateCompleted', e.target.value)}
                                            />
                                            <Button
                                                color="lte-mpLBlue"
                                                onClick={() => updateItemValue(lpi.id, 'dateCompleted', today)}
                                            >
                                                Today
                                            </Button>
                                            <Button
                                                color="lte-mpLRed"
                                                onClick={() => updateItemValue(lpi.id, 'dateCompleted', '')}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                {savingById[lpi.id] ? (
                                    <div className="text-mpOrange mt-1">Saving…</div>
                                ) : null}

                                {shouldWarn ? (
                                    <div className="text-mpLRed mt-1">
                                        Date completed cannot be before date assigned.
                                    </div>
                                ) : null}
                            </div>
                        );
                        })}
                </div>
            )}
        </div>
    );
}

function DetailsBodyFooter(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const { loaded, attemptingClose, setAttemptingClose, centers, stampParams,
        instructorOptions, selectedAppointment, relInfo, learningPlanOptions, upcomingExamsItem,
        relevantAssignments, submitted, submissionStatus, handleClose, handleSubmit,
        lessons, refreshLearningPlans, updateLearningPlanItems, updateLearningPlan, setOriginalLPItemMap } = props;

    // Get initial values - memoized to avoid recreating on every render
    const initialValues = useMemo(() => ({
        instructors: getSelectedInstructors(relevantAssignments, instructorOptions),
        specialNotesSchedule: selectedAppointment.special_notes_schedule || '',
        nPages: selectedAppointment.n_pages || 0,
        nLessons: selectedAppointment.n_lessons || 0,
        learningPlanOptions, // Will be used in handleSubmit to check what has changed
        selectedLearningPlan: (learningPlanOptions && learningPlanOptions.length > 0) ? learningPlanOptions[0] : { value: -1, label: 'No learning plans found', obj: {} },
        hideCompleted: true,
        stampsAmount: 0,
        stampsGiven: parseInt(selectedAppointment.stamps_given || 0) === 1 ? true : false,
        stampsNotes: '',
        hasExams: parseInt(upcomingExamsItem?.has_exams || 0) === 1 ? true : false,
        upcomingExamsNotes: upcomingExamsItem?.notes || '',
        sessionNotes: selectedAppointment.session_notes || '',
        hwNotes: selectedAppointment.hw_notes || '',
        status: getInitSelectedStatus(selectedAppointment.status),
        createFlag: false,
        flagTag: { value: -1, label: 'Please select...' },
        flagCenter: getInitSelectedCenter(selectedAppointment.center, centers),
        flagCenterUnlock: false,
        flagNotes: ''
    }), [relevantAssignments, instructorOptions, selectedAppointment, learningPlanOptions, upcomingExamsItem, centers]);

    // Use useFormik hook - must be called before any conditional returns
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: handleSubmit
    });

    // Handle formik reinitialization when props change
    useEffect(() => {
        if(loaded && selectedAppointment && instructorOptions.length > 0){
            const currentSelectedId = formik.values.selectedLearningPlan?.value;
            const preservedSelected = (Array.isArray(learningPlanOptions) && currentSelectedId !== undefined)
                ? (learningPlanOptions.find(lp => lp.value === currentSelectedId) || learningPlanOptions[0])
                : ((learningPlanOptions && learningPlanOptions.length > 0) ? learningPlanOptions[0] : { value: -1, label: 'No learning plans found', obj: {} });
            formik.setValues({
                instructors: getSelectedInstructors(relevantAssignments, instructorOptions),
                specialNotesSchedule: selectedAppointment.special_notes_schedule || '',
                nPages: selectedAppointment.n_pages || 0,
                nLessons: selectedAppointment.n_lessons || 0,
                learningPlanOptions,
                selectedLearningPlan: preservedSelected,
                hideCompleted: true,
                stampsAmount: 0,
                stampsGiven: parseInt(selectedAppointment.stamps_given || 0) === 1 ? true : false,
                stampsNotes: '',
                hasExams: parseInt(upcomingExamsItem?.has_exams || 0) === 1 ? true : false,
                upcomingExamsNotes: upcomingExamsItem?.notes || '',
                sessionNotes: selectedAppointment.session_notes || '',
                hwNotes: selectedAppointment.hw_notes || '',
                status: getInitSelectedStatus(selectedAppointment.status),
                createFlag: false,
                flagTag: { value: -1, label: 'Please select...' },
                flagCenter: getInitSelectedCenter(selectedAppointment.center, centers),
                flagCenterUnlock: false,
                flagNotes: ''
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAppointment, instructorOptions, relevantAssignments, learningPlanOptions, upcomingExamsItem, centers, loaded]);

    if (!loaded) {
        return (
            <ModalBodyFooter>
                <Modal.Body>
                    <ModalLoading />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        color="lte-mpLRed"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </ModalBodyFooter>
        );
    } else if (submitted) {
        return (
            <ModalBodyFooter>
                <Modal.Body>
                    <h4>
                        Updated successfully!
                    </h4>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        color="lte-mpLRed"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </ModalBodyFooter>
        );
    } else if(formik.isSubmitting){
        return renderSubmitting(submissionStatus, formik.setSubmitting);
    } else if(attemptingClose){
        return (
                            <ModalBodyFooter>
                                <Modal.Body>
                                    <h4>
                                        Are you sure you would like to close the form?
                                    </h4>
                                </Modal.Body>
                                <Modal.Footer>
                                    <div className="flex flex-row gap-x-2 flex-wrap">
                                        <Button
                                            color="lte-mpLRed"
                                            onClick={handleClose}
                                            style={{ marginRight: "5px" }}
                                        >
                                            Close Form
                                        </Button>
                                        <Button
                                            color="lte-mpLBlue"
                                            onClick={() => setAttemptingClose(false)}
                                        >
                                            Back to Form
                                        </Button>
                                    </div>
                                </Modal.Footer>
                            </ModalBodyFooter>
        );
    } else {
        return (
            <ModalBodyFooter>
                                <Modal.Body>

                                    <h2>Scheduling</h2>

                                    <br />

                                    {/* SECTION 1 */}
                                    <div className="flex flex-row gap-x-4">
                                        {/* S1 COLUMN 1 */}
                                        <div className="w-1/2">
                                            <div className="grid grid-cols-1 gap-y-2">
                                                <h4>Appointment Info</h4>
                                                <div className="ml-1 text-sm">
                                                    <div><b>Student:</b> {selectedAppointment.studentName}</div>
                                                    <div><b>Time:</b>&nbsp;
                                                        {formatDateFull(selectedAppointment.date_time)}&nbsp;
                                                        from {formatTime(selectedAppointment.startTime)}&nbsp;
                                                        to {formatTime(parseInt(selectedAppointment.endTime))}
                                                    </div>
                                                    <div><b>Center:</b> {selectedAppointment.centerName}</div>
                                                </div>
                                            </div>

                                            <br />

                                            <div className="flex flex-row gap-x-4">
                                                <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                                    <h4>Status</h4>
                                                    <SelectSingle
                                                        id="details-status"
                                                        name="status"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.status}
                                                        options={statusOptions}
                                                    />
                                                    {formik.errors.status && (
                                                        <ErrorMessage color="mpLRed">
                                                            {formik.errors.status}
                                                        </ErrorMessage>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {/* END S1 COLUMN 1 */}
                                        {/* S1 COLUMN 2 */}
                                        <div className="w-1/2">
                                            <h4>Instructors</h4>
                                            <SelectMulti
                                                id="details-instructors"
                                                name="instructors"
                                                onChange={(e) => {
                                                    if (e.target.value.length <= 5) formik.handleChange(e);
                                                }}
                                                value={formik.values.instructors}
                                                options={instructorOptions}
                                            />
                                            {formik.values.instructors.length >= 5 ?
                                                <>
                                                    <br />
                                                    <div className="text-mpOrange">
                                                        Maximum 5 instructors
                                                    </div>
                                                </> : null
                                            }

                                            <br />

                                            <TooltipWrapper
                                                tooltipText={
                                                    <div>
                                                        This will update the scheduling notes for the selected appointment.
                                                        The icon for scheduling notes is <i className="fas fa-exclamation text-mpGreen" />
                                                    </div>
                                                }
                                            >
                                                <h4 className="text-mpLBlue">Scheduling Notes</h4>
                                            </TooltipWrapper>
                                            <FormikControl
                                                id="details-special-notes-schedule"
                                                as="textarea"
                                                name="specialNotesSchedule"
                                                onChange={formik.handleChange}
                                                value={formik.values.specialNotesSchedule}
                                                placeholder="This will update the scheduling notes (green !) for this appointment. Max 250 characters"
                                            />
                                            {formik.errors.specialNotesSchedule && (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.specialNotesSchedule}
                                                </ErrorMessage>
                                            )}
                                        </div>
                                        {/* END S1 COLUMN 2 */}
                                    </div>
                                    {/* END SECTION 1 */}

                                    <br />
                                    <hr />
                                    <br />

                                    <h2>Work Completed</h2>
                                    <br />

                                    {/* SECTION 2 */}
                                    <div className="flex flex-row gap-x-4">
                                        {/* S2 COLUMN 1 */}
                                        <div className="w-1/2">
                                            <div className="flex flex-row gap-x-4">
                                                <div className="grid grid-cols-1 gap-y-2">
                                                    <h4>Active Learning Plans</h4>
                                                    <SelectSingle
                                                        id="details-selected-lp"
                                                        name="selectedLearningPlan"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.selectedLearningPlan}
                                                        options={learningPlanOptions}
                                                    />
                                                    <div className="flex flex-row gap-x-4">
                                                        <TooltipWrapper
                                                            tooltipText={"Hides lessons that were marked as completed when the form was opened."}
                                                        >
                                                            <Check
                                                                id="details-hideCompleted"
                                                                name="hideCompleted"
                                                                color="mpLRed"
                                                                label={
                                                                    <div className="text-mpLBlue">
                                                                        Hide Completed
                                                                    </div>
                                                                }
                                                                checked={formik.values.hideCompleted}
                                                                onChange={formik.handleChange}
                                                            />
                                                        </TooltipWrapper>
                                                    </div>
                                                    {formik.values.selectedLearningPlan?.value !== -1 ? (
                                                        <AppointmentLearningPlanEditor
                                                            formik={formik}
                                                            lessons={lessons}
                                                            updateLearningPlanItems={updateLearningPlanItems}
                                                            updateLearningPlan={updateLearningPlan}
                                                            refreshLearningPlans={refreshLearningPlans}
                                                            setOriginalLPItemMap={setOriginalLPItemMap}
                                                        />
                                                    ) : (
                                                        <div className="text-mpOrange">No learning plans found.</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {/* END S2 COLUMN 1 */}
                                        {/* S2 COLUMN 2 */}
                                        <div className="w-1/2">
                                            <div className="flex flex-row gap-x-4 items-start">
                                                <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                                    <h6># Pages Completed</h6>
                                                    <FormikControl
                                                        id="details-n-pages"
                                                        name="nPages"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.nPages}
                                                        placeholder="Min 0, Max 50"
                                                    />
                                                    {formik.errors.nPages && (
                                                        <ErrorMessage color="mpLRed">
                                                            {formik.errors.nPages}
                                                        </ErrorMessage>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                                    <h6># Lessons Completed</h6>
                                                    <FormikControl
                                                        id="details-n-lessons"
                                                        name="nLessons"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.nLessons}
                                                        placeholder="Min 0, Max 10"
                                                    />
                                                    {formik.errors.nLessons && (
                                                        <ErrorMessage color="mpLRed">
                                                            {formik.errors.nLessons}
                                                        </ErrorMessage>
                                                    )}
                                                </div>
                                            </div>

                                            <br />

                                            <div className="flex flex-row gap-x-4">
                                                <div className="w-1/2">
                                                    <TooltipWrapper
                                                        tooltipText={
                                                            <>
                                                                <div>Heads up! This will give stamps on <u>each</u> form submission.</div>
                                                                <br />
                                                                <div>If you are just updating other fields, leave this at 0.</div>
                                                            </>
                                                        }
                                                    >
                                                        <h4 className="text-mpLBlue">Stamps To Give*</h4>
                                                    </TooltipWrapper>
                                                    <FormikControl
                                                        id="details-form-stamps"
                                                        name="stampsAmount"
                                                        value={formik.values.stampsAmount}
                                                        onChange={formik.handleChange}
                                                        placeholder="Min 0, Max 100"
                                                    />
                                                    {formik.errors.stampsAmount && (
                                                        <ErrorMessage color="mpLRed">
                                                            {formik.errors.stampsAmount}
                                                        </ErrorMessage>
                                                    )}
                                                    <Check
                                                        id="details-form-stamps-given-bool"
                                                        name="stampsGiven"
                                                        color="mpLRed"
                                                        checked={formik.values.stampsGiven}
                                                        onChange={formik.handleChange}
                                                        label={<TooltipWrapper
                                                            tooltipText={
                                                                <>
                                                                    <div>
                                                                        Have stamps been given for this appointment?
                                                                    </div>
                                                                    <br />
                                                                    <div>
                                                                        Will be marked as true if the form is submitted and "Stamps to Give" is not zero.
                                                                    </div>
                                                                    <br />
                                                                    <div>
                                                                        When this is false, the stamps button appears purple on the
                                                                        appointments table. When true, it appears light blue.
                                                                    </div>
                                                                </>
                                                            }
                                                        >
                                                            <div className="text-mpLBlue">Stamps Given?</div>
                                                        </TooltipWrapper>}
                                                    />
                                                </div>
                                                <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                                    {renderRecommendedStamps(formik.values)}
                                                </div>
                                            </div>

                                            {parseInt(formik.values.nPages) + parseInt(formik.values.nLessons) !== 0 &&
                                                parseInt(formik.values.stampsAmount) === 0 &&
                                                !formik.values.stampsGiven ? (
                                                <div className="row no-mar">
                                                    <div className="col text-mpOrange">
                                                        Warning! Student has completed work, but has not yet received stamps!
                                                        Are you sure "Stamps To Give" should be 0?
                                                    </div>
                                                </div>
                                            ) : null
                                            }

                                            <br />

                                            <div className="flex flex-row gap-x-4">
                                                <div className="grid grid-cols-1 gap-y-2 w-full">
                                                    <TooltipWrapper
                                                        tooltipText="Notes will be added to the Stamps Log entry"
                                                    >
                                                        <h4 className="text-mpLBlue">Stamps Notes</h4>
                                                    </TooltipWrapper>
                                                    <FormikControl
                                                        id="stamps-notes"
                                                        name="stampsNotes"
                                                        value={formik.values.stampsNotes}
                                                        onChange={formik.handleChange}
                                                        placeholder="Max. 100 characters"
                                                    />
                                                    {formik.errors.stampsNotes && (
                                                        <ErrorMessage color="mpLRed">
                                                            {formik.errors.stampsNotes}
                                                        </ErrorMessage>
                                                    )}
                                                </div>
                                            </div>

                                            <br />

                                            <div className="flex flex-row gap-x-4">
                                                <div className="grid grid-cols-1 gap-y-2">
                                                    {/* The arguments needed to be adjusted since this function is
                                                adopted from the StampsModal helpers */}
                                                    {renderChanges({ object: selectedAppointment.studentObject },
                                                        { value: 'Give Stamps' },
                                                        formik.values.stampsAmount, !formik.errors.stampsAmount,
                                                        stampParams)}
                                                </div>
                                            </div>
                                        </div>
                                        {/* END S2 COLUMN 2 */}
                                    </div>
                                    {/* END SECTION 2 */}

                                    <br />
                                    <hr />
                                    <br />

                                    <h2>Notes</h2>
                                    <br />

                                    {/* SECTION 3 */}
                                    <div className="flex flex-row gap-x-4">
                                        {/* S3 COLUMN 1 */}
                                        <div className="w-1/2">
                                            <div className="grid grid-cols-1 gap-y-2 w-full">
                                                <TooltipWrapper
                                                    tooltipText="Session notes can be viewed by the parent."
                                                >
                                                    <h4 className="text-mpLBlue">Session Notes</h4>
                                                </TooltipWrapper>
                                                <FormikControl
                                                    id="details-session-notes"
                                                    name="sessionNotes"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.sessionNotes}
                                                    as="textarea"
                                                    placeholder="Notes to be shared with parents. Max 1,500 characters"
                                                />
                                                {formik.errors.sessionNotes && (
                                                    <ErrorMessage color="mpLRed">
                                                        {formik.errors.sessionNotes}
                                                    </ErrorMessage>
                                                )}
                                            </div>

                                            <br />

                                            <div className="grid grid-cols-1 gap-y-2 w-full">
                                                <h4>Homework Notes</h4>
                                                <FormikControl
                                                    id="details-homework-notes"
                                                    name="hwNotes"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.hwNotes}
                                                    as="textarea"
                                                    placeholder="To be used if the student worked on homework during their session. Max 300 characters"
                                                />
                                                {formik.errors.hwNotes && (
                                                    <ErrorMessage color="mpLRed">
                                                        {formik.errors.hwNotes}
                                                    </ErrorMessage>
                                                )}
                                            </div>

                                            <br />

                                            <div className="grid grid-cols-1 gap-y-2 w-full">
                                                <h4>Upcoming Exams</h4>
                                                <div className="flex flex-row gap-x-4">
                                                    <div className="grid grid-cols-1 gap-y-2">
                                                        <Check
                                                            id="details-has-exams"
                                                            name="hasExams"
                                                            color="mpLRed"
                                                            label="Has Exams?"
                                                            checked={formik.values.hasExams}
                                                            onChange={formik.handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                {formik.values.hasExams &&
                                                    <div className="flex flex-row gap-x-4">
                                                        <div className="grid grid-cols-1 gap-y-2 w-full">
                                                            <FormikControl
                                                                id="details-upcoming-exams-notes"
                                                                name="upcomingExamsNotes"
                                                                onChange={formik.handleChange}
                                                                value={formik.values.upcomingExamsNotes}
                                                                as="textarea"
                                                                placeholder="Any notes pertaining to a studen'ts upcoming exams (if applicable). Max 300 characters"
                                                            />
                                                            {formik.errors.upcomingExamsNotes && (
                                                                <ErrorMessage color="mpLRed">
                                                                    {formik.errors.upcomingExamsNotes}
                                                                </ErrorMessage>
                                                            )}
                                                        </div>
                                                    </div>
                                                }
                                            </div>

                                            <br />
                                        </div>
                                        {/* END S3 COLUMN 1 */}
                                        {/* S3 COLUMN 2 */}
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            {renderRelevantNotes(relInfo.combinedNotes)}
                                        </div>
                                        {/* END S3 COLUMN 3 */}
                                    </div>
                                    {/* END SECTION 3 */}

                                    <br />
                                    <hr />
                                    <br />

                                    <h2>Flags</h2>
                                    <br />
                                    {/* SECTION 4 */}
                                    <div className="flex flex-row gap-x-4">
                                        {/* S4 COLUMN 1 */}
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            <div className="grid grid-cols-1 gap-y-2 w-full">
                                                <Check
                                                    id="details-create-flag"
                                                    name="createFlag"
                                                    color="mpLRed"
                                                    label="Create Flag"
                                                    checked={formik.values.createFlag}
                                                    onChange={formik.handleChange}
                                                />
                                            </div>
                                            {formik.values.createFlag && (
                                                <div className="grid grid-cols-1 gap-y-2 w-full">
                                                    <h4>Tag</h4>
                                                    <SelectSingle
                                                        id="details-flag-tag"
                                                        name="flagTag"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.flagTag}
                                                        options={flagTags}
                                                    />
                                                    {formik.errors.flagTagValid && (
                                                        <ErrorMessage color="mpLRed">
                                                            {formik.errors.flagTagValid}
                                                        </ErrorMessage>
                                                    )}

                                                    <br />

                                                    <h4>Center</h4>
                                                    <SelectSingle
                                                        id="details-flag-center"
                                                        name="flagCenter"
                                                        disabled={!formik.values.flagCenterUnlock}
                                                        onChange={formik.handleChange}
                                                        value={formik.values.flagCenter}
                                                        options={getCenterOptions(centers)}
                                                    />
                                                    <div className="text-mpOrange">
                                                        Center defaults to the center that this appointment was at.
                                                    </div>
                                                    {formik.errors.flagCenterValid && (
                                                        <ErrorMessage color="mpLRed">
                                                            {formik.errors.flagCenterValid}
                                                        </ErrorMessage>
                                                    )}
                                                    <Check
                                                        id="details-flag-center-unlocked"
                                                        name="flagCenterUnlock"
                                                        color="mpLRed"
                                                        label="Change Center"
                                                        checked={formik.values.flagCenterUnlock}
                                                        onChange={formik.handleChange}
                                                    />

                                                    <br />

                                                    <h4>Flag Notes</h4>
                                                    <FormikControl
                                                        as="textarea"
                                                        id="details-flag-notes"
                                                        name="flagNotes"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.flagNotes}
                                                        placeholder="Extra comments about the flag in case the tag doesn't suffice. Max 200 characters"
                                                    />
                                                    {formik.errors.flagNotes && (
                                                        <ErrorMessage color="mpLRed">
                                                            {formik.errors.flagNotes}
                                                        </ErrorMessage>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {/* END S4 COLUMN 1 */}
                                        {/* S4 COLUMN 2 */}
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            {formik.values.createFlag && renderPastFlags(relInfo.flags)}
                                        </div>
                                        {/* END S4 COLUMN 2 */}
                                    </div>
                                    {/* END SECTION 4 */}
                                </Modal.Body>
                                <Modal.Footer>
                                    <div className="flex flex-row gap-x-2 flex-wrap">
                                        {!formik.isValid && parseInt(formik.submitCount) && !formik.isSubmitting ?
                                            (
                                                <div className="text-mpLRed">
                                                    One or more fields needs to be corrected.
                                                </div>
                                            ) : null
                                        }
                                        {formik.status && !formik.isSubmitting ?
                                            <div className="text-mpLRed">
                                                {formik.status}
                                            </div> : null
                                        }
                                        <Button
                                            color="lte-mpLRed"
                                            disabled={formik.isSubmitting}
                                            onClick={handleClose}
                                        >
                                            Close
                                        </Button>
                                        <Button
                                            color="lte-mpLBlue"
                                            onClick={formik.handleSubmit}
                                            disabled={formik.isSubmitting}
                                        >
                                            Submit
                                        </Button>
                                        <Button
                                            color="lte-mpLBlue"
                                            onClick={(e) => {
                                                formik.setFieldValue('status', { value: 'Completed', label: 'Completed' });
                                                formik.handleSubmit(e);
                                            }}
                                            disabled={formik.isSubmitting}
                                        >
                                            Mark Completed &amp; Submit
                                        </Button>
                                    </div>
                                </Modal.Footer>
            </ModalBodyFooter>
        );
    }
}

// Set displayName and _modalComponentType so findComponentByType can find this component
DetailsBodyFooter.displayName = 'BodyFooter';
DetailsBodyFooter._modalComponentType = 'BodyFooter';

export default connect(null, {

})(DetailsBodyFooter);